import {
  Box,
  Drawer,
  DrawerProps,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { FC, ReactElement, ReactNode, useState } from 'react';
import { useDrawersContext } from './DrawersContext';
import { WbDrawerActions } from './WbDrawerActions';
import { WbDrawerContextProvider } from './WbDrawerContext';
import { Theme } from '@material-ui/core';
import { WbTooltip } from '../WbTooltip';

const useStyles = makeStyles(
  (theme: Theme) => ({
    paper: (props: { size?: WbDrawerSize }) => ({
      padding: 0,
      width: `${props.size}%` || '75%',
      overflowX: 'hidden',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    }),
    card: {
      width: '100%',
      display: 'flex',
      height: '100%',
      flexDirection: 'column',
      background: theme.palette.background.default,
      borderRadius: theme.spacing(0.5),
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      overflowX: 'auto',
      overflowY: 'hidden',
      color: theme.palette.black,

      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      background: theme.palette.background.paper,
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      height: 48,
      padding: '8px 16px',
    },
    headerTitleContent: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flex: '1 1 0',
      overflow: 'hidden',
    },
    content: {
      padding: 12,
    },
  }),
  { name: 'WbDrawer' },
);
export type WbDrawerSize = 100 | 95 | 90 | 75 | 50 | 30;

interface WbDrawerProps extends Omit<DrawerProps, 'title'> {
  children?: ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  canClose?: boolean;
  title: string | ReactElement;
  headerActions?: ReactNode;
  actions?: ReactNode;
  hideHeader?: boolean;
  hideCloseButton?: boolean;
  size?: WbDrawerSize;
}

export const WbDrawer: FC<WbDrawerProps> = ({
  children,
  title,
  open,
  setOpen,
  canClose = true,
  headerActions,
  actions,
  onClose,
  hideHeader = false,
  hideCloseButton = false,
  size = 75,
  ...drawerProps
}) => {
  const { onCloseAll } = useDrawersContext();
  const classes = useStyles({ size });

  // while a request to prevent the drawer from closing has been made and not retired yet, there are no requests when === 0
  const [lockRequests, setLockRequests] = useState(0);

  return (
    <WbDrawerContextProvider
      value={{
        setOpen,
        canClose: lockRequests === 0 && canClose,
        open,
        lockClosure: () => setLockRequests(prev => prev + 1),
        unlockClosure: () => setLockRequests(prev => prev - 1),
      }}
    >
      <Drawer
        disableRestoreFocus
        classes={{
          paper: classes.paper,
        }}
        anchor="right"
        open={open}
        onClose={() => {
          if (!canClose) return;
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          onCloseAll ? onCloseAll() : setOpen(false);
        }}
        {...drawerProps}
      >
        <Box className={classes.card}>
          {!hideHeader && (
            <Box className={classes.header}>
              <Box className={classes.headerContent}>
                <Box className={classes.headerTitleContent}>
                  {typeof title === 'string' ? (
                    <WbTooltip title={title}>
                      <Typography variant="h6" noWrap>
                        {title}
                      </Typography>
                    </WbTooltip>
                  ) : (
                    title
                  )}
                </Box>
                {headerActions}
                {!hideCloseButton && (
                  <IconButton
                    disabled={!canClose}
                    aria-label="clear"
                    onClick={() => {
                      if (!canClose) return;
                      setOpen(false);
                    }}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}
          {children}
          {actions && <WbDrawerActions>{actions}</WbDrawerActions>}
        </Box>
      </Drawer>
    </WbDrawerContextProvider>
  );
};
